import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import useProjectDetailsQuery from '@buildwise/pages/project/hooks/useProjectDetailsQuery';
import { ClearFiltersOptions, FilterType } from 'components/common/filter/Filter';
import useUserData from 'hooks/useUserData';
import { orderBy, uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

function useRetrosFilters() {
  const [filters, setFilters] = useState<FilterType[]>([]);
  const { companyId } = useUserData();
  const { projectId = '' } = useParams();
  const { projectArticles } = useProjectDetailsQuery({
    projectId,
    shouldFetch: false,
  });
  const {
    activeUniqueEntries,
    config,
    isLoading: isFiltersLoading,
  } = useConfigQuery({
    companyId,
    configName: CONFIGS.PROJECT_PHASES.configName,
  });
  const activeUniqueOptions = useMemo(
    () => activeUniqueEntries.map((entry) => entry.entryCode),
    [activeUniqueEntries],
  );
  const phasesWithCount = useMemo(() => {
    let phasesWithCount: Record<string, number> = {};
    projectArticles.forEach((article) => {
      if (!article.projectPhases || article.projectPhases?.length === 0) {
        phasesWithCount['(blank)'] = phasesWithCount['(blank)']
          ? phasesWithCount['(blank)'] + 1
          : 1;
      } else {
        article.projectPhases?.forEach((phase) => {
          phasesWithCount[phase] = phasesWithCount[phase] ? phasesWithCount[phase] + 1 : 1;
        });
      }
    });
    return phasesWithCount;
  }, [projectArticles]);
  const options = useMemo(() => {
    const options = uniq([
      ...activeUniqueOptions,
      ...orderBy(Object.keys(phasesWithCount).filter((item) => item !== '(blank)')),
    ]);

    if (Object.keys(phasesWithCount).includes('(blank)')) {
      options.push('(blank)');
    }

    return options;
  }, [activeUniqueEntries, phasesWithCount]);

  useEffect(() => {
    setFilters((prevState) => [
      {
        ...prevState,
        category: 'projectPhases',
        categoryIndex: 0,
        displayName: config?.description || 'Project Phases',
        options:
          options.map((option) => ({
            label: option,
            value: option,
            badgeValue: phasesWithCount[option] || 0,
          })) || [],
        allOptions: options || [],
        showSelectAll: true,
        defaultOpen: true,
        selectedOptions: prevState[0]?.selectedOptions || [],
      },
    ]);
  }, [config, phasesWithCount, options]);

  const clearFilters = useCallback((options?: ClearFiltersOptions) => {
    if (options?.defaultClearFiltersCallback) {
      options.defaultClearFiltersCallback?.();
    } else {
      setFilters((prevState) => {
        return prevState.map((filter) => ({
          ...filter,
          selectedOptions: [],
          selectedValue: filter.defaultSelectedValue,
          defaultSelectedValue: filter.defaultSelectedValue,
        }));
      });
    }
  }, []);

  return {
    filters,
    isFiltersLoading,
    phasesWithCount,
    clearFilters,
    setFilters,
  };
}

export default useRetrosFilters;
